import {
  Avatar,
  Badge,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { AppPage } from '@markku/components'
import type { VisitWithRelations } from '@markku/types'
import {
  formatDate,
  formatRelativeTime,
  getCategoryColor,
  getCategoryLabel,
  isToday,
} from '@markku/utils'
import { IconCalendarEvent, IconClock } from '@tabler/icons-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React from 'react'
import { VisitListItem } from '../../components/VisitListItem'
import { propertyHooks, visitHooks } from '../../lib/trpcHooks'
import { ServicePromotionCard } from '../../modules/subscription/components'

function VisitsPage() {
  const navigate = useNavigate()

  // Fetch all visits
  const { data: visitsData, isLoading } = visitHooks.useList({
    pageSize: 100,
    orderBy: 'startTime',
    orderDirection: 'asc',
  })

  // Check if user has any active subscription
  const { data: propertiesResponse } = propertyHooks.useList()
  const isPremium =
    propertiesResponse?.data.some((property) =>
      property.subscriptions?.some((sub) => sub.status === 'active'),
    ) ?? false

  const visits = visitsData?.data || []

  // Split visits and identify next upcoming visit
  const now = new Date()
  const upcomingVisits = visits.filter((visit: VisitWithRelations) => {
    return new Date(visit.startTime) >= now
  })
  const nextVisit = upcomingVisits[0] // The first upcoming visit is the next one

  const pastVisits = visits
    .filter((visit: VisitWithRelations) => {
      return new Date(visit.startTime) < now
    })
    .sort((a, b) => {
      // Sort past visits in descending order
      return new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
    })

  // Group visits by month/year for better visual organization
  const groupVisitsByMonth = (visits: VisitWithRelations[]) => {
    const grouped: Record<string, VisitWithRelations[]> = {}

    visits.forEach((visit) => {
      const date = new Date(visit.startTime)
      const key = date.toLocaleDateString('fi-FI', {
        month: 'long',
        year: 'numeric',
      })

      if (!grouped[key]) {
        grouped[key] = []
      }
      grouped[key].push(visit)
    })

    return grouped
  }

  const renderNextVisit = (visit: VisitWithRelations | undefined) => {
    if (!visit) {
      // Show promotion card for non-premium users
      if (!isPremium) {
        return (
          <ServicePromotionCard
            title="Markku hoitaa kotisi huollon puolestasi"
            description="Markku.fi on kodin huoltopalvelu pääkaupunkiseudulla. Tilauksen aktivoinnin jälkeen Markku käy kodissasi säännöllisesti hoitamassa sopimukseen kuuluvat huoltotehtävät ja tarvittaessa pieniä korjaus- ja asennustöitä."
            benefits={[
              'Säännölliset huoltokäynnit ympäri vuoden',
              'Ammattitaitoinen huoltohenkilö hoitaa tehtävät',
              'Huoltohistoria ja käyntikirjaukset',
            ]}
            showAvailabilityNote
          />
        )
      }

      // Show regular empty state for premium users
      return (
        <Card withBorder radius="md" p="xl" bg="gray.0">
          <Center>
            <Stack align="center" gap="xs">
              <IconCalendarEvent
                size={48}
                stroke={1.5}
                color="var(--mantine-color-gray-5)"
              />
              <Text c="dimmed" size="md" fw={500}>
                Ei tulevia käyntejä
              </Text>
              <Text c="dimmed" size="sm">
                Seuraava käynti sovitaan erikseen
              </Text>
            </Stack>
          </Center>
        </Card>
      )
    }

    const visitDate = new Date(visit.startTime)
    const endTime = new Date(visit.endTime)
    const isTodayVisit = isToday(visitDate)
    const isTomorrow = isToday(
      new Date(visitDate.getTime() - 24 * 60 * 60 * 1000),
    )

    // Extract unique task categories
    const taskCategories = visit.visitTasks
      ? [
          ...new Set(
            visit.visitTasks
              .map((task) => task.maintenanceTask?.category)
              .filter(Boolean),
          ),
        ]
      : []

    // Get attendee info for avatar
    const attendee = visit.visitAttendees?.[0]?.user
    const attendeeInitials = attendee
      ? `${attendee.firstName[0]}${attendee.lastName[0]}`
      : 'M' // Default to M for Markku

    return (
      <Card
        radius="lg"
        shadow="xs"
        style={{
          background: 'linear-gradient(135deg, #FFF4E6 0%, #FFE8CC 100%)',
          border: '1px solid var(--mantine-color-brandOrange-2)',
          cursor: 'pointer',
        }}
        p="md"
        onClick={() => navigate({ to: `/visit/${visit.id}` })}
      >
        <Stack gap="sm">
          <Group gap="xs">
            <IconCalendarEvent
              size={20}
              color="var(--mantine-color-brandOrange-6)"
            />
            <Text fw={500}>Markun seuraava käynti</Text>
          </Group>

          <Group justify="space-between" align="center">
            <Text size="lg" fw={700} c="dark">
              {formatDate(visitDate)} klo{' '}
              {visitDate.toLocaleTimeString('fi-FI', {
                hour: '2-digit',
                minute: '2-digit',
              })}
              -
              {endTime.toLocaleTimeString('fi-FI', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
            <Group gap="sm">
              {isTodayVisit && (
                <Badge size="lg" color="green" variant="light">
                  Tänään
                </Badge>
              )}
              {isTomorrow && (
                <Badge size="lg" color="blue" variant="light">
                  Huomenna
                </Badge>
              )}
              <Avatar size="sm" radius="xl" color="brandOrange">
                {attendeeInitials}
              </Avatar>
            </Group>
          </Group>

          <Group gap="xs">
            <IconClock
              size={16}
              stroke={1.5}
              color="var(--mantine-color-brandOrange-6)"
            />
            <Text size="sm" c="dimmed">
              {formatRelativeTime(visitDate)}
            </Text>
          </Group>

          {/* Task category badges */}
          {taskCategories.length > 0 ? (
            <Group gap="xs">
              {taskCategories.map((category) => (
                <Badge
                  key={category}
                  size="md"
                  variant="light"
                  color={getCategoryColor(category as string)}
                >
                  {getCategoryLabel(category as string)}
                </Badge>
              ))}
            </Group>
          ) : null}

          {/* Notes if available */}
          {visit.notes && (
            <Text size="sm" c="dimmed" lineClamp={2}>
              {visit.notes}
            </Text>
          )}
        </Stack>
      </Card>
    )
  }

  const renderPastVisits = (visits: VisitWithRelations[]) => {
    if (visits.length === 0) {
      return null
    }

    const groupedVisits = groupVisitsByMonth(visits)
    const elements: React.ReactNode[] = []

    Object.entries(groupedVisits).forEach(([monthYear, monthVisits], index) => {
      elements.push(
        <Divider
          key={`month-${monthYear}`}
          label={
            <Text fw={600} size="md" c="dimmed">
              {monthYear}
            </Text>
          }
          labelPosition="center"
          mt={index > 0 ? 'xl' : 0}
          mb="lg"
        />,
      )

      monthVisits.forEach((visit) => {
        elements.push(<VisitListItem key={visit.id} visit={visit} />)
      })
    })

    return <Stack gap="lg">{elements}</Stack>
  }

  return (
    <>
      <AppPage title="Käynnit" subtitle="Markun käynnit" backTarget="/home">
        <Stack gap="xl">
          {/* Loading state */}
          {isLoading && (
            <Center py="xl">
              <Loader size="md" />
            </Center>
          )}

          {!isLoading && (
            <>
              {/* Next upcoming visit - prominently displayed */}
              {renderNextVisit(nextVisit)}

              {/* Past visits section */}
              {pastVisits.length > 0 && (
                <Stack gap="md">
                  <Title order={4} c="dimmed">
                    Käyntihistoria
                  </Title>
                  {renderPastVisits(pastVisits)}
                </Stack>
              )}
            </>
          )}
        </Stack>
      </AppPage>
    </>
  )
}

export const Route = createFileRoute('/_app/visits')({
  component: VisitsPage,
})
